import { OutputPresetId } from '@/content/instagram'
import type { Rect } from '@/core/geometry'

export enum CollageFamily {
  Grid = 'grid',
  Prints = 'prints',
}

export enum CollageTemplateId {
  TwoSideBySide = 'grid-2-side',
  TwoStacked = 'grid-2-stacked',
  ThreeBigLeft = 'grid-3-big-left',
  ThreeBigTop = 'grid-3-big-top',
  ThreeColumns = 'grid-3-columns',
  ThreeRows = 'grid-3-rows',
  FourSquare = 'grid-4-square',
  FourBigLeft = 'grid-4-big-left',
  SixGrid = 'grid-6',
  NineGrid = 'grid-9',
  PrintsTwoOffset = 'prints-2-offset',
  PrintsThreeBeach = 'prints-3-beach',
  PrintsFourStagger = 'prints-4-stagger',
  PrintsFiveFan = 'prints-5-fan',
}

export interface CollageCellSpec {
  rect: Rect
  z: number
  rotationDeg: number
}

export interface CollageTemplate {
  id: CollageTemplateId
  family: CollageFamily
  label: string
  cells: readonly CollageCellSpec[]
  goodFor: readonly OutputPresetId[]
}

const cell = (x: number, y: number, width: number, height: number, z: number): CollageCellSpec => ({
  rect: { x, y, width, height },
  z,
  rotationDeg: 0,
})

export const COLLAGE_TEMPLATES = [
  {
    id: CollageTemplateId.TwoSideBySide,
    family: CollageFamily.Grid,
    label: 'Side by side',
    cells: [cell(0, 0, 0.5, 1, 0), cell(0.5, 0, 0.5, 1, 1)],
    goodFor: [OutputPresetId.Square, OutputPresetId.Landscape],
  },
  {
    id: CollageTemplateId.TwoStacked,
    family: CollageFamily.Grid,
    label: 'Stacked',
    cells: [cell(0, 0, 1, 0.5, 0), cell(0, 0.5, 1, 0.5, 1)],
    goodFor: [OutputPresetId.Portrait, OutputPresetId.Story],
  },
  {
    id: CollageTemplateId.ThreeBigLeft,
    family: CollageFamily.Grid,
    label: 'Big left',
    cells: [cell(0, 0, 0.6, 1, 0), cell(0.6, 0, 0.4, 0.5, 1), cell(0.6, 0.5, 0.4, 0.5, 2)],
    goodFor: [OutputPresetId.Square, OutputPresetId.Landscape],
  },
  {
    id: CollageTemplateId.ThreeBigTop,
    family: CollageFamily.Grid,
    label: 'Big top',
    cells: [cell(0, 0, 1, 0.6, 0), cell(0, 0.6, 0.5, 0.4, 1), cell(0.5, 0.6, 0.5, 0.4, 2)],
    goodFor: [OutputPresetId.Portrait, OutputPresetId.Square],
  },
  {
    id: CollageTemplateId.ThreeColumns,
    family: CollageFamily.Grid,
    label: 'Three columns',
    cells: [cell(0, 0, 1 / 3, 1, 0), cell(1 / 3, 0, 1 / 3, 1, 1), cell(2 / 3, 0, 1 / 3, 1, 2)],
    goodFor: [OutputPresetId.Landscape, OutputPresetId.Square],
  },
  {
    id: CollageTemplateId.ThreeRows,
    family: CollageFamily.Grid,
    label: 'Three rows',
    cells: [cell(0, 0, 1, 1 / 3, 0), cell(0, 1 / 3, 1, 1 / 3, 1), cell(0, 2 / 3, 1, 1 / 3, 2)],
    goodFor: [OutputPresetId.Portrait, OutputPresetId.Story],
  },
  {
    id: CollageTemplateId.FourSquare,
    family: CollageFamily.Grid,
    label: 'Four square',
    cells: [
      cell(0, 0, 0.5, 0.5, 0),
      cell(0.5, 0, 0.5, 0.5, 1),
      cell(0, 0.5, 0.5, 0.5, 2),
      cell(0.5, 0.5, 0.5, 0.5, 3),
    ],
    goodFor: [OutputPresetId.Square, OutputPresetId.Portrait],
  },
  {
    id: CollageTemplateId.FourBigLeft,
    family: CollageFamily.Grid,
    label: 'Big left plus three',
    cells: [
      cell(0, 0, 0.55, 1, 0),
      cell(0.55, 0, 0.45, 1 / 3, 1),
      cell(0.55, 1 / 3, 0.45, 1 / 3, 2),
      cell(0.55, 2 / 3, 0.45, 1 / 3, 3),
    ],
    goodFor: [OutputPresetId.Landscape, OutputPresetId.Square],
  },
  {
    id: CollageTemplateId.SixGrid,
    family: CollageFamily.Grid,
    label: 'Six grid',
    cells: Array.from({ length: 6 }, (_, index) => cell(index % 3 / 3, Math.floor(index / 3) / 2, 1 / 3, 0.5, index)),
    goodFor: [OutputPresetId.Square, OutputPresetId.Portrait],
  },
  {
    id: CollageTemplateId.NineGrid,
    family: CollageFamily.Grid,
    label: 'Nine grid',
    cells: Array.from({ length: 9 }, (_, index) => cell(index % 3 / 3, Math.floor(index / 3) / 3, 1 / 3, 1 / 3, index)),
    goodFor: [OutputPresetId.Square, OutputPresetId.Portrait],
  },
  {
    id: CollageTemplateId.PrintsTwoOffset,
    family: CollageFamily.Prints,
    label: 'Two offset prints',
    cells: [cell(0.08, 0.08, 0.55, 0.68, 0), cell(0.4, 0.3, 0.52, 0.62, 1)],
    goodFor: [OutputPresetId.Square, OutputPresetId.Portrait],
  },
  {
    id: CollageTemplateId.PrintsThreeBeach,
    family: CollageFamily.Prints,
    label: 'Three beach prints',
    cells: [cell(0.05, 0.08, 0.35, 0.84, 0), cell(0.48, 0.18, 0.47, 0.72, 1), cell(0.28, 0.02, 0.32, 0.4, 2)],
    goodFor: [OutputPresetId.Landscape, OutputPresetId.Original],
  },
  {
    id: CollageTemplateId.PrintsFourStagger,
    family: CollageFamily.Prints,
    label: 'Four staggered prints',
    cells: [
      cell(0.06, 0.06, 0.42, 0.44, 0),
      cell(0.5, 0.12, 0.42, 0.44, 1),
      cell(0.12, 0.48, 0.42, 0.44, 2),
      cell(0.56, 0.5, 0.38, 0.42, 3),
    ],
    goodFor: [OutputPresetId.Square, OutputPresetId.Portrait],
  },
  {
    id: CollageTemplateId.PrintsFiveFan,
    family: CollageFamily.Prints,
    label: 'Five-print fan',
    cells: [
      cell(0.02, 0.18, 0.34, 0.58, 0),
      cell(0.64, 0.18, 0.34, 0.58, 1),
      cell(0.17, 0.08, 0.34, 0.6, 2),
      cell(0.49, 0.08, 0.34, 0.6, 3),
      cell(0.33, 0.24, 0.34, 0.64, 4),
    ],
    goodFor: [OutputPresetId.Landscape, OutputPresetId.Square],
  },
] as const satisfies readonly CollageTemplate[]

export enum GradientPresetId {
  Sky = 'sky',
  Sunset = 'sunset',
  Blush = 'blush',
  Forest = 'forest',
  Night = 'night',
  Citrus = 'citrus',
}

export enum CollageColorPresetId {
  White = 'white',
  Warm = 'warm',
  Blush = 'blush',
  Sand = 'sand',
  Sage = 'sage',
  Charcoal = 'charcoal',
}

export interface CollageColorPreset {
  id: CollageColorPresetId
  label: string
  color: string
}

export const COLLAGE_COLOR_PRESETS = [
  { id: CollageColorPresetId.White, label: 'White', color: '#ffffff' },
  { id: CollageColorPresetId.Warm, label: 'Warm', color: '#fbf8f2' },
  { id: CollageColorPresetId.Blush, label: 'Blush', color: '#f6dbe1' },
  { id: CollageColorPresetId.Sand, label: 'Sand', color: '#e8dcc8' },
  { id: CollageColorPresetId.Sage, label: 'Sage', color: '#cfd8cc' },
  { id: CollageColorPresetId.Charcoal, label: 'Charcoal', color: '#252525' },
] as const satisfies readonly CollageColorPreset[]

export interface GradientPreset {
  id: GradientPresetId
  label: string
  colors: readonly [string, string]
  angleDeg: number
}

export const GRADIENT_PRESETS = [
  { id: GradientPresetId.Sky, label: 'Sky', colors: ['#9fcbe8', '#4e8fc7'], angleDeg: 180 },
  { id: GradientPresetId.Sunset, label: 'Sunset', colors: ['#f7b267', '#f4845f'], angleDeg: 135 },
  { id: GradientPresetId.Blush, label: 'Blush', colors: ['#f8d7df', '#d9b8e8'], angleDeg: 160 },
  { id: GradientPresetId.Forest, label: 'Forest', colors: ['#b7c9a8', '#496a4f'], angleDeg: 180 },
  { id: GradientPresetId.Night, label: 'Night', colors: ['#20243b', '#57628c'], angleDeg: 145 },
  { id: GradientPresetId.Citrus, label: 'Citrus', colors: ['#ffe29a', '#ff9f68'], angleDeg: 120 },
] as const satisfies readonly GradientPreset[]

const HEX_COLOR_PATTERN = /^#[\da-f]{6}$/i
const GRID_MINIMUM_COVERAGE = 0.98

function rectsOverlap(first: Rect, second: Rect): boolean {
  return first.x < second.x + second.width
    && first.x + first.width > second.x
    && first.y < second.y + second.height
    && first.y + first.height > second.y
}

export function validateCollageTemplates(
  templates: readonly CollageTemplate[] = COLLAGE_TEMPLATES,
): void {
  const seenIds = new Set<CollageTemplateId>()
  for (const template of templates) {
    if (!Object.values(CollageTemplateId).includes(template.id)) {
      throw new Error(`Collage template configuration has unknown id "${String(template.id)}"`)
    }
    if (seenIds.has(template.id)) throw new Error(`Collage template configuration has duplicate id "${template.id}"`)
    seenIds.add(template.id)
    if (!Object.values(CollageFamily).includes(template.family)) {
      throw new Error(`Collage template "${template.id}" has unknown family "${String(template.family)}"`)
    }
    if (!template.label.trim()) throw new Error(`Collage template "${template.id}" requires a label`)
    if (template.cells.length === 0) throw new Error(`Collage template "${template.id}" requires at least one cell`)
    if (template.goodFor.length === 0) throw new Error(`Collage template "${template.id}" requires at least one good-for preset`)
    if (new Set(template.goodFor).size !== template.goodFor.length) {
      throw new Error(`Collage template "${template.id}" has duplicate good-for presets`)
    }
    for (const presetId of template.goodFor) {
      if (!Object.values(OutputPresetId).includes(presetId)) {
        throw new Error(`Collage template "${template.id}" has unknown good-for preset "${String(presetId)}"`)
      }
    }
    const seenZ = new Set<number>()
    for (const [index, candidate] of template.cells.entries()) {
      const { x, y, width, height } = candidate.rect
      if (![x, y, width, height].every(Number.isFinite)
        || x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 1 || y + height > 1) {
        throw new Error(`Collage template "${template.id}" cell ${index + 1} must stay inside the canvas`)
      }
      if (!Number.isInteger(candidate.z) || seenZ.has(candidate.z)) {
        throw new Error(`Collage template "${template.id}" cell z values must be unique integers`)
      }
      seenZ.add(candidate.z)
      if (!Number.isFinite(candidate.rotationDeg) || Math.abs(candidate.rotationDeg) > 15) {
        throw new Error(`Collage template "${template.id}" cell rotation must be from -15 to 15 degrees`)
      }
    }
    if (template.family === CollageFamily.Grid) {
      for (let first = 0; first < template.cells.length; first += 1) {
        for (let second = first + 1; second < template.cells.length; second += 1) {
          const firstCell = template.cells[first]
          const secondCell = template.cells[second]
          if (!firstCell || !secondCell) throw new Error(`Collage template "${template.id}" cell configuration is missing`)
          if (rectsOverlap(firstCell.rect, secondCell.rect)) {
            throw new Error(`Grid collage template "${template.id}" cells must not overlap`)
          }
        }
      }
      const coverage = template.cells.reduce((total, candidate) => (
        total + candidate.rect.width * candidate.rect.height
      ), 0)
      if (coverage < GRID_MINIMUM_COVERAGE) {
        throw new Error(`Grid collage template "${template.id}" must cover at least 98% of the canvas`)
      }
    }
  }
  for (const id of Object.values(CollageTemplateId)) {
    if (!seenIds.has(id)) throw new Error(`Collage template configuration is missing id "${id}"`)
  }
  for (const family of Object.values(CollageFamily)) {
    if (!templates.some((template) => template.family === family)) {
      throw new Error(`Collage template configuration is missing family "${family}"`)
    }
  }
  for (const presetId of Object.values(OutputPresetId)) {
    if (!templates.some((template) => template.goodFor.includes(presetId))) {
      throw new Error(`Collage template configuration has no template for output preset "${presetId}"`)
    }
  }
}

export function validateGradientPresets(
  presets: readonly GradientPreset[] = GRADIENT_PRESETS,
): void {
  const seenIds = new Set<GradientPresetId>()
  for (const preset of presets) {
    if (!Object.values(GradientPresetId).includes(preset.id)) {
      throw new Error(`Gradient preset configuration has unknown id "${String(preset.id)}"`)
    }
    if (seenIds.has(preset.id)) throw new Error(`Gradient preset configuration has duplicate id "${preset.id}"`)
    seenIds.add(preset.id)
    if (!preset.label.trim()) throw new Error(`Gradient preset "${preset.id}" requires a label`)
    if (!preset.colors.every((color) => HEX_COLOR_PATTERN.test(color))) {
      throw new Error(`Gradient preset "${preset.id}" requires two six-digit hex colors`)
    }
    if (!Number.isFinite(preset.angleDeg)) throw new Error(`Gradient preset "${preset.id}" requires a finite angle`)
  }
  for (const id of Object.values(GradientPresetId)) {
    if (!seenIds.has(id)) throw new Error(`Gradient preset configuration is missing id "${id}"`)
  }
}

export function validateCollageColorPresets(
  presets: readonly CollageColorPreset[] = COLLAGE_COLOR_PRESETS,
): void {
  const seenIds = new Set<CollageColorPresetId>()
  for (const preset of presets) {
    if (!Object.values(CollageColorPresetId).includes(preset.id)) {
      throw new Error(`Collage color preset configuration has unknown id "${String(preset.id)}"`)
    }
    if (seenIds.has(preset.id)) throw new Error(`Collage color preset configuration has duplicate id "${preset.id}"`)
    seenIds.add(preset.id)
    if (!preset.label.trim()) throw new Error(`Collage color preset "${preset.id}" requires a label`)
    if (!HEX_COLOR_PATTERN.test(preset.color)) {
      throw new Error(`Collage color preset "${preset.id}" requires a six-digit hex color`)
    }
  }
  for (const id of Object.values(CollageColorPresetId)) {
    if (!seenIds.has(id)) throw new Error(`Collage color preset configuration is missing id "${id}"`)
  }
}

export function getCollageTemplate(id: CollageTemplateId): CollageTemplate {
  const template = COLLAGE_TEMPLATES.find((candidate) => candidate.id === id)
  if (!template) throw new Error(`Unknown collage template: "${id}"`)
  return template
}

export function getGradientPreset(id: GradientPresetId): GradientPreset {
  const preset = GRADIENT_PRESETS.find((candidate) => candidate.id === id)
  if (!preset) throw new Error(`Unknown gradient preset: "${id}"`)
  return preset
}
